
interface CampaignStep {
    id: string
    component: string
    order: number
    label: string
    description?: string
    isOptional?: boolean
    autoCompleteIfEmpty?: boolean
}

interface BaseCampaignConfig {
    name: string
    description?: string
    icon?: string
    steps: CampaignStep[]
    fieldMapping: Record<string, string[]>
    optionalSteps?: string[]
    [key: string]: any
}

export const ACA_CAMPAIGN_CONFIG: Omit<BaseCampaignConfig, 'campaignId'> & {
    campaignType: 'aca-health-insurance'
    campaignNames: string[]
} = {
    campaignType: 'aca-health-insurance',
    campaignNames: ['ACA Health Insurance', 'aca-health-insurance'],
    name: 'ACA Health Insurance',
    description: 'Enrollment for Affordable Care Act Health Insurance',
    icon: 'Activity',
    steps: [
        {
            id: 'client-validation',
            component: 'client-validation',
            order: 0,
            label: 'Client Validation',
            description: 'Client details and eligibility verification',
            isOptional: false
        },
        {
            id: 'enrollment-data',
            component: 'enrollment-data',
            order: 1,
            label: 'Enrollment Data',
            description: 'Contact info, spouse, and dependents',
            isOptional: false
        },
        {
            id: 'employment-plan',
            component: 'employment-plan',
            order: 2,
            label: 'Employment & Plan',
            description: 'Employment, income, and health plan selection',
            isOptional: false
        },
        {
            id: 'agent-consents',
            component: 'agent-consents',
            order: 3,
            label: 'Agent & Consents',
            description: 'Agent details, consents, documents, and signature',
            isOptional: false
        },
        {
            id: 'compliance',
            component: 'compliance',
            order: 4,
            label: 'Compliance & Authorization',
            description: 'CMS authorization and compliance verification',
            isOptional: false
        },
        {
            id: 'follow-up',
            component: 'follow-up',
            order: 5,
            label: 'Follow-Up',
            description: 'Additional insurance, referrals, and recruiting',
            isOptional: false
        }
    ],
    fieldMapping: {
        'client-validation': [
            'clientFirstName', 'clientLastName', 'clientSuffix', 'clientDOB', 'clientSSN',
            'clientState', 'clientZip', 'verificationComplete', 'stateEligibility',
            'availableCarriers', 'clientEligibilityStatus', 'clientEligibilityMessage'
        ],
        'enrollment-data': [
            'clientAddress', 'clientAddressLine2', 'clientCity', 'clientState', 'clientZip',
            'clientPhone', 'clientEmail', 'clientSex', 'isSmoker', 'isPregnant',
            'maritalStatus', 'spouseFirstName', 'spouseLastName', 'spouseDOB', 'spouseSSN',
            'hasDependents', 'dependentsList', 'householdSize'
        ],
        'employment-plan': [
            'employmentType', 'householdSize', 'annualIncome', 'employerName', 'typeOfWork',
            'carrierDesired', 'customerNotes', 'selectedCarrier'
        ],
        'agent-consents': [
            'referralAgentFirstName', 'referralAgentLastName', 'agentSuffix', 'managerName',
            'referralAgentEmail', 'applicationNotes',
            'cmsVoiceConsent', 'cmsVoiceConsentAt', 'cmsWrittenConsent', 'cmsWrittenConsentAt',
            'carrierVoiceConsent', 'carrierVoiceConsentAt', 'carrierWrittenConsent', 'carrierWrittenConsentAt',
            'idDocument', 'documentSkipped',
            'onMedicaidMedicare', 'isHomeless',
            'clientSignature', 'drawableSignature1', 'drawableSignature2', 'drawableSignature3', 'drawableSignature4',
            'typedFullNameSignature', 'applicationDate', 'incomeAttestation', 'noGiftsAttestation', 'messageConsent'
        ],
        'compliance': [
            'authorizedUserConsent', 'permissionGiven', 'hasInsurance', 'termsAgreement',
            'cmsAuthorizationOption'
        ],
        'follow-up': [
            'wantsDentalInsurance', 'wantsVisionInsurance', 'wantsLifeInsurance', 'wantsAncillaryInsurance',
            'referHealthInsurance', 'referDentalInsurance', 'referVisionInsurance',
            'referLifeInsurance', 'referAncillaryInsurance', 'referMedicareInsurance',
            'healthReferralEmails',
            'clientWantsJob', 'clientWantsToReferForJob', 'recruitingReferralEmail'
        ]
    },
    minAge: 18,
    maxAge: 64,
    requiredDocuments: ['idDocument']
}

export const ACA_MESSAGES = {
    title: 'ACA Health Insurance Enrollment',
    subtitle: 'Get affordable health coverage today',
    success: 'Enrollment submitted successfully!'
}

export const isACACampaign = (campaignId: string) => {
    return campaignId === 'aca-health-insurance' || campaignId === 'campaign-4' || campaignId === 'campaign-5'
}

// State-specific income ranges for auto-suggestion (based on FPL and typical ACA subsidy eligibility)
export const STATE_INCOME_RANGES: Record<string, { min: number; max: number }[]> = {
    // Format: [householdSize - 1] = { min, max }
    'default': [
        { min: 15000, max: 60000 },   // 1 person
        { min: 20000, max: 81000 },   // 2 people
        { min: 25000, max: 102000 },  // 3 people
        { min: 30000, max: 123000 },  // 4 people
        { min: 35000, max: 144000 },  // 5 people
        { min: 40000, max: 165000 },  // 6 people
        { min: 45000, max: 186000 },  // 7 people
        { min: 50000, max: 207000 },  // 8 people
    ]
}
